import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import {
    PointDirection,
    segmentType,
    SketchBowing,
    SketchRoughness,
    SketchRoughnessOffset,
} from '@src/slide/domain/ui-model/element-modeles/property/border/sketch/path-processor/constants';
import { BorderSketchType } from '@src/slide/domain/ui-model/element-modeles/property/border/constants';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { PathParser } from '@tencent/alloy-renderer';
import { NormalizedSegment } from '@tencent/alloy-renderer/dist/lib/path';
import { createGraphicsParser, FillMode, GraphicsType, ICustomPath } from '@tencent/alloy-graphics';
import {
    IShapeModelStyle,
} from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/shape-model-props.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import {
    IPictureModelStyle,
} from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/picture-model-props.interface';
import { getRoughenPath } from '@src/slide/domain/ui-model/element-modeles/property/border/sketch/path-processor/rough-path';
import { getSplitPath } from '@src/slide/domain/ui-model/element-modeles/property/border/sketch/path-processor/split-path';
import { FillType } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { isSketchStyle } from '@src/slide/domain/ui-model/element-modeles/property/border/sketch/sketch-style';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';

export interface IProcessorOptions {
    model: IShapeModel | IPictureModel,
    type?: BorderSketchType,
    style?: Partial<IShapeModelStyle | IPictureModelStyle>,
}

export class PathProcessor {
    private static seedMap = new Map<string, Array<number>>();
    private static sizeFactor: number;

    public static generateSketchPathList(options: IProcessorOptions): ICustomPath[] {
        if (!isSketchStyle(options.type)) {
            return [];
        }
        const pathLists = [];
        const pathSegments = [];
        PathProcessor.duplicateAndCombinePath(options, pathLists, pathSegments);
        PathProcessor.splitPath(options, pathLists, pathSegments);
        PathProcessor.roughenPath(options, pathLists, pathSegments);

        return pathLists;
    }

    /**
     * 根据 path 原本的填充和描边方式，重新组合 path
     * @private
     */
    private static duplicateAndCombinePath(
        options: IProcessorOptions,
        pathLists: ICustomPath[],
        pathSegments: NormalizedSegment[][],
    ) {
        const { model, style } = options;
        const {
            width: modelWidth,
            height: modelHeight,
            controlValues,
            bgShape,
            pathList,
        } = model?.props.style || {};
        const width = Math.round((style?.width ?? modelWidth));
        const height = Math.round((style?.height ?? modelHeight));
        const { sketchStyle } = PathProcessor.getBorder(model);
        const parser = createGraphicsParser({
            type: style?.bgShape ?? sketchStyle?.bgShape ?? bgShape ?? GraphicsType.RECT,
            width,
            height,
            controlValues: style?.controlValues ?? controlValues,
            pathList: sketchStyle?.pathList ?? pathList,
        });
        const svgArray = parser.svgPathInRect();
        svgArray.forEach((path) => {
            if (!path) return;
            const pathWidth = width / (path.scaleX ?? 1);
            const pathHeight = height / (path.scaleY ?? 1);
            PathProcessor.sizeFactor = 1 / (path.scaleX || 1);
            const noFillPath = {
                width: pathWidth,
                height: pathHeight,
                pathString: path.svgPath,
                fill: FillMode.NONE,
                stroke: 1,
            };
            const noStrokePath = {
                width: pathWidth,
                height: pathHeight,
                pathString: path.svgPath,
                stroke: 0,
                fill: path.fillMode,
            };
            const segments = PathParser.parse(path.svgPath);

            if (PathProcessor.shouldRenderFillPath(model, path, segments) && !path.notStroke) {
                // 如果 path 同时支持填充和描边，需要拆成两份 path，分别绘制填充和描边
                // 先添加填充 path，再添加描边 path
                pathLists.push(noStrokePath);
                pathLists.push(noFillPath);
                pathSegments.push(segments);
                pathSegments.push(segments);
            } else {
                const singlePath = {
                    width: pathWidth,
                    height: pathHeight,
                    pathString: path.svgPath,
                    stroke: path.notStroke ? 0 : 1,
                    fill: path.fillMode,
                };
                pathLists.push(singlePath);
                pathSegments.push(segments);
            }
        });
    }

    private static shouldRenderFillPath(
        model: IShapeModel | IPictureModel,
        path,
        segments: NormalizedSegment[],
    ): boolean {
        const isClosedCurve = (segments.length > 1 && (segments[0].x === segments[segments.length - 1].x)
                && (segments[0].y === segments[segments.length - 1].y))
            || segments[segments.length - 1].cmd === segmentType.ClosePath;
        return path.fillMode !== FillMode.NONE
            && isClosedCurve
            && PathProcessor.hasFill(model);
    }

    private static hasFill(model: IShapeModel | IPictureModel): boolean {
        return model.getBackgroundFill?.().getFillType() !== FillType.NoFill;
    }

    private static splitPath(
        options: IProcessorOptions,
        pathLists: ICustomPath[],
        pathSegments: NormalizedSegment[][],
    ) {
        // 草绘类型为"曲形"，不需要拆分 path
        if (options.type === BorderSketchType.CURVED) {
            return;
        }
        pathSegments.forEach((segments, index) => {
            const path = getSplitPath(segments, options.type as BorderSketchType, PathProcessor.sizeFactor);
            pathSegments[index] = PathParser.parse(path);
            pathLists[index].pathString = path;
        });
    }

    /**
     * 根据 segment 草绘路径后重新生成 pathString
     * @private
     */
    private static roughenPath(
        options: IProcessorOptions,
        pathLists: ICustomPath[],
        pathSegments: NormalizedSegment[][],
    ) {
        const { model, type = BorderSketchType.NONE } = options;
        pathSegments.forEach((segments, index) => {
            const [fillSeed, strokeSeed] = PathProcessor.getSeed(options) || [];
            const path = pathLists[index];
            const seed = path.fill !== FillMode.NONE ? fillSeed : strokeSeed;
            const scale = PathProcessor.getScale(model, type, path);
            const roughOptions = {
                roughness: SketchRoughness[type] * scale,
                bowing: SketchBowing[type] * scale,
                roughnessOffset: SketchRoughnessOffset[type] * scale,
                direction: PathProcessor.getDirection(model, path),
                seed,
                sizeFactor: PathProcessor.sizeFactor,
            };
            pathLists[index].pathString = getRoughenPath(segments, roughOptions);
        });
    }

    private static getScale(model: IShapeModel | IPictureModel, type: BorderSketchType, path): number {
        if (model.type === UIModelType.IMAGE) {
            return 1;
        }
        if (!PathProcessor.hasFill(model)) {
            return model.isPlaceholder && type === BorderSketchType.CURVED ? 0.2 : 1;
        }
        if (path.fill !== FillMode.NONE) {
            return 1;
        }
        return type === BorderSketchType.CURVED ? 0.4 : 1;
    }

    private static getDirection(model: IShapeModel | IPictureModel, path): PointDirection {
        const direction = model.isPlaceholder ? PointDirection.UP : PointDirection.DOWN;
        if (!PathProcessor.hasFill(model)) {
            return direction;
        }
        if (path.fill !== FillMode.NONE) {
            return direction;
        }
        return model.isPlaceholder ? PointDirection.DOWN : PointDirection.UP;
    }

    private static getSeed(options: IProcessorOptions): Array<number> | undefined {
        if (options.type !== BorderSketchType.CURVED) {
            return;
        }
        const key = PathProcessor.generateKey(options);
        if (PathProcessor.seedMap.has(key)) {
            return PathProcessor.seedMap.get(key);
        }
        const fillSeed = Math.random();
        const strokeSeed = Math.random();
        PathProcessor.seedMap.set(key, [fillSeed, strokeSeed]);
        return [fillSeed, strokeSeed];
    }

    private static generateKey(options: IProcessorOptions): string {
        const { model, type, style } = options;
        const {
            width: modelWidth,
            height: modelHeight,
            controlValues: modelControlValues,
            bgShape,
        } = model?.props.style || {};
        const width = style?.width ?? modelWidth;
        const height = style?.height ?? modelHeight;
        const controlValues = style?.controlValues ?? modelControlValues;
        return `${type}_${bgShape}_${width}_${height}_${controlValues?.toString?.()}`;
    }

    private static getBorder(model: IShapeModel | IPictureModel) {
        return (model as IMultiplyModel | IElementBaseModel).getBorder?.()
            .getBorderInfo() ?? {};
    }
}
